import React, { useState, useEffect } from 'react';
import { List, Avatar, Spin, Empty, Typography, Tag } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { getEmployees } from '@/api/employees';
import { Employee } from '@/types/resources/employee';

const { Text } = Typography;

interface EmployeeListTabProps {
  onSelect: (employee: Employee) => void;
  searchTerm: string;
}

export const EmployeeListTab: React.FC<EmployeeListTabProps> = ({ onSelect, searchTerm }) => {
  const { t } = useTranslation('employees');
  const { firestoreUser } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load employees
  useEffect(() => {
    if (!firestoreUser?.workspace?.id) return;

    const loadEmployees = async () => {
      try {
        setIsLoading(true);
        const employeesData = await getEmployees({
          firestoreUser
        });
        setEmployees(employeesData.employees);
      } catch (error) {
        console.error('Error loading employees:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadEmployees();
  }, [firestoreUser?.workspace?.id]);

  // Filter employees based on search term
  useEffect(() => {
    if (!employees || !Array.isArray(employees)) {
      setFilteredEmployees([]);
      return;
    }
    
    if (!searchTerm.trim()) {
      setFilteredEmployees(employees);
    } else {
      const filtered = employees.filter(employee =>
        employee?.user?.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee?.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee?.role?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEmployees(filtered);
    }
  }, [employees, searchTerm]);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">{t('loading', 'Loading employees...')}</Text>
        </div>
      </div>
    );
  }

  if (filteredEmployees.length === 0) {
    return (
      <Empty
        description={
          searchTerm.trim() 
            ? t('noEmployeesFound', 'No employees found matching your search')
            : t('noEmployees', 'No employees found in this workspace')
        }
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <List
      itemLayout="horizontal"
      dataSource={filteredEmployees}
      style={{ maxHeight: '400px', overflowY: 'auto' }}
      renderItem={(employee) => {
        if (!employee) return null;
        
        return (
          <List.Item
            onClick={() => onSelect(employee)}
          style={{
            padding: '12px 16px',
            cursor: 'pointer',
            borderBottom: '1px solid #f0f0f0',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <List.Item.Meta
            avatar={
              <Avatar 
                icon={<UserOutlined />}
                size="large"
              />
            }
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text strong style={{ fontSize: '16px' }}>
                  {employee.user?.displayName || employee.user?.email || 'Employee'}
                </Text>
                {employee.status && (
                  <Tag 
                    color={employee.status === 'active' ? 'green' : 'red'}
                  >
                    {employee.status}
                  </Tag>
                )}
              </div>
            }
            description={
              <div>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  {employee.user?.email || 'No email'}
                </Text>
                {employee.role && (
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      💼 {employee.role}
                    </Text>
                  </div>
                )}
                {employee.user?.phoneNumber && (
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      📞 {employee.user.phoneNumber}
                    </Text>
                  </div>
                )}
              </div>
            }
          />
        </List.Item>
        );
      }}
    />
  );
};
